import type { ContentfulComponent, ContentfulModule, Sys } from 'libs/contentful/types';
import type { EnrichedModule } from './propInjection';
import type { ResolvedModule, SelectedPersonalization } from './types';

/**
 * Narrows a ContentfulComponent to ContentfulModule.
 *
 * PageProps fields (hero, modulesCollection, etc.) are typed as specific prop
 * unions that extend ContentfulComponent but not ContentfulModule. At runtime
 * these objects carry all the fields the experiment system needs (sys, __typename,
 * experiment refs). This guard bridges the two type hierarchies.
 */
export function isContentfulModule(entry: ContentfulComponent | null | undefined): entry is ContentfulModule {
  if (typeof entry !== 'object' || entry === null) return false;
  return 'sys' in entry && 'id' in entry.sys && '__typename' in entry;
}

/**
 * Checks if a module has a `statsigExperiment` field with a valid sys.id,
 * indicating it participates in a Statsig A/B test.
 */
export function hasStatsigContainer(module: unknown): module is ContentfulModule & {
  statsigExperiment: { sys: Sys };
} {
  if (typeof module !== 'object' || module === null) return false;
  const obj = module as Record<string, unknown>;
  const statsigExperiment = obj.statsigExperiment;
  if (typeof statsigExperiment !== 'object' || statsigExperiment === null) return false;
  const sys = (statsigExperiment as Record<string, unknown>).sys;
  if (typeof sys !== 'object' || sys === null) return false;
  return typeof (sys as Record<string, unknown>).id === 'string';
}

/**
 * Checks if a module has an `ntExperiencesCollection` with at least one item,
 * indicating it participates in a Ninetailed experiment or personalization.
 */
export function hasNinetailedExperiences(module: unknown): module is ContentfulModule & {
  ntExperiencesCollection: { items: unknown[] };
} {
  if (typeof module !== 'object' || module === null) return false;
  const obj = module as Record<string, unknown>;
  const collection = obj.ntExperiencesCollection;
  if (typeof collection !== 'object' || collection === null) return false;
  const items = (collection as Record<string, unknown>).items;
  return Array.isArray(items) && items.length > 0;
}

/**
 * Checks if an entry has a `modulesCollection` field, identifying it as
 * a container entry like PageSection that holds child modules.
 */
export function hasModulesCollection(entry: unknown): entry is { modulesCollection: { items: unknown[] } } {
  if (typeof entry !== 'object' || entry === null) return false;
  const obj = entry as Record<string, unknown>;
  const collection = obj.modulesCollection;
  if (typeof collection !== 'object' || collection === null) return false;
  return Array.isArray((collection as Record<string, unknown>).items);
}

/**
 * Checks if an entry has been enriched with `_exposure` data from
 * experiment resolution, indicating it is a ResolvedModule.
 */
export function hasSchema(module: EnrichedModule): module is EnrichedModule & { schema: { jsonLd: object } } {
  return 'schema' in module && typeof module.schema?.jsonLd === 'object' && module.schema.jsonLd !== null;
}

export function isPersonalizedEntry(entry: unknown): entry is ResolvedModule & { _exposure: SelectedPersonalization } {
  if (typeof entry !== 'object' || entry === null) return false;
  const obj = entry as Record<string, unknown>;
  const exposure = obj._exposure;
  if (typeof exposure !== 'object' || exposure === null) return false;
  const exp = exposure as Record<string, unknown>;
  return (
    typeof exp.experienceId === 'string' &&
    typeof exp.variantIndex === 'number' &&
    typeof exp.variants === 'object' &&
    exp.variants !== null
  );
}
